import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dropdown,
  Button,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { API_URL } from "../../../config";
import moment from "moment";
import { truncate } from "../../../utils/helper";
import { Link } from "react-router-dom";

const filterOptions = ["Current Status", "Upcoming", "Ongoing", "Completed"];
const themeOptions = [
  "AI/ML",
  "Robotics & Automation",
  "Internet of Things (IoT)",
  "Blockchain / Web3",
  "Cybersecurity",
  "AR / VR / XR",
  "EdTech",
  "HealthTech / MedTech",
  "Fitness & Wellness",
  "BioTech",
  "CleanTech",
  "Renewable Energy",
  "ClimateTech",
  "AgriTech",
  "Waste Management",
  "RetailTech",
  "FinTech",
  "PropTech",
  "SpaceTech",
  "Quantum Computing",
  "Semiconductor & Chip Design",
  "Legal Tech",
  "Marketing Tech (MarTech)",
];

const renderPagination = (
  currentPage,
  totalPages,
  totalRecord,
  handlePageChange,
  itemsPerPage,
) => {
  const maxPagesToShow = 5;
  let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

  // Adjust start page if we're near the end
  if (endPage - startPage + 1 < maxPagesToShow) {
    startPage = Math.max(0, endPage - maxPagesToShow + 1);
  }

  return (
    <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
      <div className="dataTables_info">
        Showing {currentPage * itemsPerPage + 1} to{" "}
        {Math.min((currentPage + 1) * itemsPerPage, totalRecord)} of{" "}
        {totalRecord} entries
      </div>
      <div className="d-flex align-items-center">
        <nav>
          <ul className="pagination pagination-gutter pagination-primary no-bg">
            {/* First Page */}
            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0}
              >
                <i className="fa fa-angle-double-left"></i>
              </button>
            </li>

            {/* Previous */}
            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
              >
                <i className="fa fa-angle-left"></i>
              </button>
            </li>

            {/* Show ellipsis if not at start */}
            {startPage > 0 && (
              <li className="page-item">
                <span className="page-link">...</span>
              </li>
            )}

            {/* Page Numbers */}
            {Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i,
            ).map((pageNum) => (
              <li
                key={pageNum}
                className={`page-item ${
                  pageNum === currentPage ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum + 1}
                </button>
              </li>
            ))}

            {/* Show ellipsis if not at end */}
            {endPage < totalPages - 1 && (
              <li className="page-item">
                <span className="page-link">...</span>
              </li>
            )}

            {/* Next */}
            <li
              className={`page-item ${
                currentPage === totalPages - 1 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages - 1}
              >
                <i className="fa fa-angle-right"></i>
              </button>
            </li>

            {/* Last Page */}
            <li
              className={`page-item ${
                currentPage === totalPages - 1 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
              >
                <i className="fa fa-angle-double-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

const StartupGrants = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("Current Status");
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [totalRecord, setTotalRecord] = useState(0);

  // For pagination
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        offset: page * itemsPerPage,
        limit: itemsPerPage,
      };
      if (statusFilter !== "Current Status") {
        params.status = statusFilter;
      }

      if (selectedTheme) {
        params.theme = selectedTheme;
      }

      if (search) {
        params.search = search;
      }
      const response = await axios.get(
        `${API_URL}/api/freshment_portal_apis/EduLearn/getGrantsList`,
        {
          params: params,
        },
      );
      if (response.data.success) {
        setData(response.data.data);
        setTotalPages(Math.ceil(response.data.totalRecords / itemsPerPage));
        setTotalRecord(response.data.totalRecords);
      } else {
        setData([]);
        setTotalPages(0);
        setTotalRecord(0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isResetting.current) {
      fetchData();
    }
  }, [page, statusFilter, selectedTheme]);

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const isResetting = useRef(false);

  // Reset all filters
  const resetFilter = useCallback(() => {
    isResetting.current = true;
    setSearch("");
    setStatusFilter("Current Status");
    setSelectedTheme(null);
    setPage(0);

    setTimeout(() => {
      isResetting.current = false;
      fetchData();
    }, 10);
  }, []);

  return (
    <div>
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">Startup Grants</h3>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="d-flex align-items-center flex-wrap search-job bg-white px-0 mb-4">
        <div className="col-xl-2 col-xxl-2 col-lg-2 col-sm-4 col-12 search-dropdown d-flex align-items-center">
          <Dropdown className="form-control border-0 style-1 h-auto">
            <Dropdown.Toggle
              as="div"
              className="fs-16 font-w500 text-primary d-flex align-items-center i-false"
            >
              {statusFilter}
              <i className="fas fa-angle-down text-primary scale5 ms-3"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu dropdown-menu-end mt-1">
              {filterOptions.map((filter) => (
                <Dropdown.Item
                  key={filter}
                  onClick={() => setStatusFilter(filter)}
                  className="text-primary"
                >
                  {filter}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="col-xl-2 col-xxl-2 col-lg-2 col-sm-4 col-12 search-dropdown d-flex align-items-center">
          <Dropdown className="form-control border-0 style-1 h-auto">
            <Dropdown.Toggle
              as="div"
              className="fs-16 font-w500 text-primary d-flex align-items-center justify-content-between i-false"
            >
              {selectedTheme ? truncate(selectedTheme, 15) : "Startup Themes"}
              <i className="fas fa-angle-down text-primary scale5 ms-3"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="dropdown-menu dropdown-menu-end mt-1"
              style={{ maxHeight: 350, overflowY: "auto" }}
            >
              {themeOptions.map((theme) => (
                <Dropdown.Item
                  key={theme}
                  className="text-primary"
                  onClick={() => setSelectedTheme(theme)}
                >
                  {theme}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Search Box */}
        <div className="col-xl-7 col-xxl-7 col-lg-7 col-12 d-md-flex job-title-search pe-0">
          <div className="input-group search-area">
            <input
              type="text"
              value={search}
              className="form-control h-auto"
              placeholder="Search grants by name, organizer, theme or location"
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="input-group-text">
              <Button onClick={handleSearch} className="btn btn-primary btn-sm">
                Search<i className="flaticon-381-search-2 ms-1" style={{ fontSize: '14px', verticalAlign: 'middle' }}></i>
              </Button>
            </span>
          </div>
        </div>

        {/* Reset Button */}
        <div className="col-xl-1 col-xxl-1 col-lg-1 col-12 d-flex align-items-center justify-content-lg-end justify-content-center">
          <button onClick={resetFilter} className="btn btn-danger btn-sm">
            Reset
          </button>
        </div>
      </div>

      {/* Grants Cards */}
      <Row>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading grants...</p>
          </div>
        ) : data.length > 0 ? (
          <>
            <Col lg={12} md={12} sm={12} className="mb-4">
              <h5>
                Showing {page * itemsPerPage + 1} to{" "}
                {Math.min((page + 1) * itemsPerPage, totalRecord)} of{" "}
                {totalRecord}{" "}
              </h5>
            </Col>
            {data.map((grant, index) => {
              // Parse themes
              const themes = grant.startup_theme
                ? grant.startup_theme.split(",").map((theme) => theme.trim())
                : [];
              
              // Resolve category and remaining tags
              const category = themes.length > 0 ? themes[0] : "Grant";
              const displayTags = themes.slice(1);

              // Get organizer initial
              const organizerInitial = grant.organizer_name
                ? grant.organizer_name.charAt(0).toUpperCase()
                : "O";

              // Clean description of HTML tags for clean rendering
              const cleanDescription = grant.description
                ? grant.description.replace(/<[^>]*>/g, "")
                : grant.eligibility_criteria
                ? grant.eligibility_criteria.replace(/<[^>]*>/g, "")
                : "Explore this startup grant opportunity, learn about its requirements, and submit your application.";

              return (
                <Col lg={6} md={6} sm={12} key={index} className="mb-4 d-flex">
                  <div
                    className="card w-100"
                    style={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.04)",
                      border: "1px solid #eceff1",
                      background: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 30px rgba(0, 0, 0, 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "none";
                      e.currentTarget.style.boxShadow =
                        "0 10px 25px rgba(0, 0, 0, 0.04)";
                    }}
                  >
                    {/* Top Gradient Bar */}
                    <div
                      style={{
                        height: "6px",
                        background:
                          "linear-gradient(90deg, #7b2cbf 0%, #4cc9f0 100%)",
                      }}
                    ></div>

                    <div className="card-body p-4 d-flex flex-column h-100">
                      {/* Top Badges Row */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        {/* Category (Primary Theme) */}
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            backgroundColor: "#f3e8ff",
                            color: "#7e22ce",
                            padding: "6px 14px",
                            borderRadius: "50px",
                            fontSize: "12px",
                            fontWeight: "700",
                            letterSpacing: "0.3px",
                          }}
                        >
                          <i
                            className="fas fa-award me-2"
                            style={{ fontSize: "12px" }}
                          ></i>
                          {category}
                        </div>

                        {/* Location and Deadline */}
                        <div className="d-flex align-items-center gap-3">
                          

                          <div
                            className="d-flex align-items-center text-muted"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            <i
                              className="far fa-calendar-alt me-2"
                              style={{ fontSize: "14px" }}
                            ></i>
                            Ends: {moment(grant.registration_end_date).format("DD-MM-YYYY")}
                          </div>
                        </div>
                      </div>

                      {/* Title */}
                      <h3
                        className="mb-2"
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#0f172a",
                          lineHeight: "1.3",
                        }}
                      >
                        {grant.name}
                      </h3>

                      <lable className="pt-3 pb-5"> 
                        <span
                            style={{
                              backgroundColor: "#e0f2fe",
                              color: "#0369a1",
                              padding: "8px 15px",
                              borderRadius: "8px",
                              fontSize: "16px",
                              fontWeight: "900",
                            }}
                          >
                         <i
                              className="fas fa-map-marker-alt me-2"
                              style={{ fontSize: "14px" }}
                            ></i> {grant.location || "Remote"}
                          </span>
                      </lable>
                      {/* Themes as Tags */}
                      <div
                        className="d-flex flex-wrap gap-1 mb-4"
                        style={{ minHeight: "32px" }}
                      >
                        {displayTags.slice(0, 3).map((theme, i) => (
                          <Badge key={i} className="me-2 mb-2">
                            {theme}
                          </Badge>
                        ))}
                        {displayTags.length > 3 && (
                          <Badge className="me-2 mb-2">
                            +{displayTags.length - 3} more
                          </Badge>
                        )}
                      </div>

                      {/* Footer Row (Organizer & View Details Button) */}
                      <div className="d-flex justify-content-between align-items-center mt-auto">
                        {/* Organizer Box */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #e2e8f0",
                            borderRadius: "14px",
                            padding: "8px 14px 8px 10px",
                            backgroundColor: "#fafafb",
                            maxWidth: "70%",
                          }}
                        >
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              backgroundColor: "#10b981",
                              borderRadius: "50%",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "700",
                              fontSize: "12px",
                              marginRight: "10px",
                              flexShrink: 0,
                              boxShadow: "0 2px 5px rgba(16, 185, 129, 0.2)",
                            }}
                          >
                            {organizerInitial}
                          </div>
                          <div className="d-flex flex-column text-truncate">
                            <span
                              style={{
                                fontSize: "9px",
                                fontWeight: "700",
                                color: "#10b981",
                                letterSpacing: "0.5px",
                                lineHeight: "1",
                              }}
                            >
                              ORGANIZER
                            </span>
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#1e293b",
                                marginTop: "2px",
                              }}
                              className="text-truncate"
                            >
                              {grant.organizer_name || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* View Details Button */}
                        <Link
                          to={`/startup-details/${grant.id}`}
                          className="btn text-white d-flex align-items-center"
                          style={{
                            backgroundColor: "#0f172a",
                            borderRadius: "12px",
                            padding: "10px 18px",
                            fontSize: "13px",
                            fontWeight: "700",
                            boxShadow: "0 4px 10px rgba(15, 23, 42, 0.15)",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#1e293b";
                            e.currentTarget.style.boxShadow =
                              "0 6px 15px rgba(15, 23, 42, 0.25)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#0f172a";
                            e.currentTarget.style.boxShadow =
                              "0 4px 10px rgba(15, 23, 42, 0.15)";
                          }}
                        >
                          <i
                            className="fas fa-play me-2"
                            style={{ fontSize: "10px" }}
                          ></i>
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}

            {renderPagination(
              page,
              totalPages,
              totalRecord,
              setPage,
              itemsPerPage,
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <div className="mb-3">
              <i className="fas fa-search fa-3x text-muted"></i>
            </div>
            <h4>No grants found</h4>
            <p className="text-muted">
              Try changing your search criteria or filters
            </p>
          </div>
        )}
      </Row>
    </div>
  );
};

export default StartupGrants;
